import type { LngLat } from "./types.ts";

const EARTH_M = 6371000;
const METERS_PER_DEG_LAT = 111_320;

export function isLngLat(value: unknown): value is LngLat {
  if (!Array.isArray(value) || value.length < 2) return false;
  const [lng, lat] = value;
  return (
    typeof lng === "number" &&
    typeof lat === "number" &&
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    lng >= -180 &&
    lng <= 180 &&
    lat >= -90 &&
    lat <= 90
  );
}

export function lineBBox(coords: LngLat[]): [number, number, number, number] {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  }
  return [minLng, minLat, maxLng, maxLat];
}

export function lineCenter(coords: LngLat[]): { lat: number; lng: number } {
  const [minLng, minLat, maxLng, maxLat] = lineBBox(coords);
  return {
    lng: (minLng + maxLng) / 2,
    lat: (minLat + maxLat) / 2,
  };
}

export function haversineM(a: LngLat, b: LngLat): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function lineDistanceM(coords: LngLat[]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += haversineM(coords[i - 1], coords[i]);
  }
  return total;
}

function lngMeters(from: LngLat, to: LngLat): number {
  const midLat = ((from[1] + to[1]) / 2) * (Math.PI / 180);
  return (to[0] - from[0]) * METERS_PER_DEG_LAT * Math.cos(midLat);
}

function latMeters(from: LngLat, to: LngLat): number {
  return (to[1] - from[1]) * METERS_PER_DEG_LAT;
}

/**
 * Closest point on segment a→b, in a local meter frame anchored at `a`.
 * Accurate for city-scale segments (the editor's snap distances).
 */
export function closestPointOnSegment(
  p: LngLat,
  a: LngLat,
  b: LngLat,
): { point: LngLat; distanceM: number; t: number } {
  const cos = Math.cos((a[1] * Math.PI) / 180);
  const lngScale = METERS_PER_DEG_LAT * Math.max(0.01, cos);
  const bx = (b[0] - a[0]) * lngScale;
  const by = (b[1] - a[1]) * METERS_PER_DEG_LAT;
  const px = (p[0] - a[0]) * lngScale;
  const py = (p[1] - a[1]) * METERS_PER_DEG_LAT;
  const len2 = bx * bx + by * by;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, (px * bx + py * by) / len2));
  const east = t * bx;
  const north = t * by;
  return {
    point: [a[0] + east / lngScale, a[1] + north / METERS_PER_DEG_LAT],
    distanceM: Math.hypot(px - east, py - north),
    t,
  };
}

/** Distance from a point to the closest location on segment a→b. */
export function pointToSegmentM(p: LngLat, a: LngLat, b: LngLat): number {
  return closestPointOnSegment(p, a, b).distanceM;
}

export function pointToLineM(p: LngLat, coords: LngLat[]): number {
  if (coords.length === 0) return Infinity;
  if (coords.length === 1) return haversineM(p, coords[0]);
  let best = Infinity;
  for (let i = 1; i < coords.length; i++) {
    best = Math.min(best, pointToSegmentM(p, coords[i - 1], coords[i]));
  }
  return best;
}

/** Arc length along the line to the nearest point on the polyline. */
export function alongLineM(p: LngLat, coords: LngLat[]): number {
  if (coords.length < 2) return 0;
  let bestDist = Infinity;
  let bestAlong = 0;
  let walked = 0;
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1];
    const b = coords[i];
    const seg = haversineM(a, b);
    const bx = lngMeters(a, b);
    const by = latMeters(a, b);
    const px = lngMeters(a, p);
    const py = latMeters(a, p);
    const len2 = bx * bx + by * by;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, (px * bx + py * by) / len2));
    const dist = Math.hypot(px - t * bx, py - t * by);
    if (dist < bestDist) {
      bestDist = dist;
      bestAlong = walked + t * seg;
    }
    walked += seg;
  }
  return bestAlong;
}

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

export function radiusToBBox(
  lat: number,
  lng: number,
  radiusM: number,
): { minLat: number; minLng: number; maxLat: number; maxLng: number } {
  const dLat = radiusM / METERS_PER_DEG_LAT;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const dLng = radiusM / (METERS_PER_DEG_LAT * Math.max(0.01, cosLat));
  return {
    minLat: Math.max(-90, lat - dLat),
    maxLat: Math.min(90, lat + dLat),
    minLng: Math.max(-180, lng - dLng),
    maxLng: Math.min(180, lng + dLng),
  };
}

export function escapeXml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

export function toGpx(title: string, coords: LngLat[]): string {
  const points = coords
    .map(
      ([lng, lat]) =>
        `      <trkpt lat="${lat.toFixed(7)}" lon="${lng.toFixed(7)}"></trkpt>`,
    )
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Mapus" xmlns="http://www.topografix.com/GPX/1/1">
  <trk>
    <name>${escapeXml(title)}</name>
    <trkseg>
${points}
    </trkseg>
  </trk>
</gpx>
`;
}
